import { z } from 'zod';
import { addressSchema, hexDataSchema, isoTimestampSchema, nonEmptyStringSchema, numericLikeSchema, evmChainSlugSchema, } from './common.js';
/** Use for DeFi endpoints scoped to one chain and wallet. */
export const chainAddressParamsSchema = z.object({
    chain: evmChainSlugSchema,
    address: addressSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
/** Use for DeFi endpoints scoped to one chain. */
export const chainParamsSchema = z.object({
    chain: evmChainSlugSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
/** Use for quote endpoints that take an input token and output token. */
export const chainTokenPairAmountParamsSchema = z.object({
    chain: evmChainSlugSchema,
    tokenIn: addressSchema,
    tokenOut: addressSchema,
    amount: nonEmptyStringSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
/** Use to request a Uniswap V2 quote. */
export const getUniswapV2QuoteParamsSchema = chainTokenPairAmountParamsSchema;
/** Use to request a Uniswap V3 quote. */
export const getUniswapV3QuoteParamsSchema = chainTokenPairAmountParamsSchema.extend({
    fee: numericLikeSchema.optional(),
}).strict();
/** Use to request a Uniswap V4 quote. */
export const getUniswapV4QuoteParamsSchema = chainTokenPairAmountParamsSchema.extend({
    fee: numericLikeSchema.optional(),
    tickSpacing: numericLikeSchema.optional(),
}).strict();
/** Use to request a Jupiter quote. */
export const getJupiterQuoteParamsSchema = z.object({
    inputMint: nonEmptyStringSchema,
    outputMint: nonEmptyStringSchema,
    amount: nonEmptyStringSchema,
    slippageBps: numericLikeSchema.optional(),
}).strict();
/** Use to request a Jupiter spot price. */
export const getJupiterPriceParamsSchema = z.object({
    inputMint: nonEmptyStringSchema,
    outputMint: nonEmptyStringSchema,
}).strict();
/** Use to request an Avantis vault price. */
export const getAvantisPriceParamsSchema = z.object({
    chain: evmChainSlugSchema,
    vault: nonEmptyStringSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
/** Use to request Pendle market info. */
export const getPendleV2InfoParamsSchema = chainParamsSchema;
/** Use to build a revoke-approval transaction. */
export const getApprovalsRevokeParamsSchema = z.object({
    chain: evmChainSlugSchema,
    address: addressSchema,
    token: addressSchema,
    spender: addressSchema,
}).strict();
/** Use for funding-rate endpoints with no required params. */
export const getFundingRatesParamsSchema = z.object({}).strict();
/** Use to request Hyperliquid funding data. */
export const getHyperliquidFundingParamsSchema = z.object({ coin: nonEmptyStringSchema.optional() }).strict();
/** Use to request dYdX funding data. */
export const getDydxFundingParamsSchema = z.object({ ticker: nonEmptyStringSchema.optional() }).strict();
/** Response shape for normalized EVM swap quotes. */
export const swapQuoteDataSchema = z.object({
    chain: evmChainSlugSchema,
    tokenIn: addressSchema,
    tokenOut: addressSchema,
    amountIn: nonEmptyStringSchema,
    amountOut: nonEmptyStringSchema,
    fee: z.number().int().nonnegative().optional(),
    tickSpacing: z.number().int().nonnegative().optional(),
    gasEstimate: nonEmptyStringSchema.optional(),
}).strict();
/** One hop in a Jupiter route plan. */
export const jupiterRoutePlanStepSchema = z.object({
    swapInfo: z.object({
        ammKey: nonEmptyStringSchema,
        label: nonEmptyStringSchema,
        inputMint: nonEmptyStringSchema,
        outputMint: nonEmptyStringSchema,
    }).strict(),
    percent: z.number(),
}).strict();
/** Response shape for Jupiter quotes. */
export const jupiterQuoteDataSchema = z.object({
    inputMint: nonEmptyStringSchema,
    outputMint: nonEmptyStringSchema,
    amountIn: nonEmptyStringSchema,
    amountOut: nonEmptyStringSchema,
    otherAmountThreshold: nonEmptyStringSchema,
    swapMode: nonEmptyStringSchema,
    priceImpactPct: nonEmptyStringSchema,
    routePlan: z.array(jupiterRoutePlanStepSchema),
}).strict();
/** Response shape for Jupiter prices. */
export const jupiterPriceDataSchema = z.object({
    price: z.number(),
}).strict();
// protocolPositionDataSchema is shared across 7 DeFi position shapes (Aave V2/V3,
// Compound V2/V3, Lido, Morpho, Curve LlamaLend) that each have distinct payloads.
// The exact-match fixture tests pin each shape per protocol per chain, so the
// catchall here intentionally defers deep shape checking to those fixtures.
/** Response shape for one protocol's positions. */
export const protocolPositionDataSchema = z.object({
    chain: evmChainSlugSchema,
    address: addressSchema,
}).catchall(z.unknown());
/** Response shape for Curve pool lists. */
export const curvePoolsDataSchema = z.object({
    chain: evmChainSlugSchema,
    pools: z.array(z.unknown()),
}).strict();
/** Response shape for Curve Llamalend market lists. */
export const curveLlamaLendMarketsDataSchema = z.object({
    chain: evmChainSlugSchema,
    markets: z.array(z.unknown()),
}).strict();
/** Response shape for Pendle info. */
export const pendleInfoDataSchema = z.object({
    chain: evmChainSlugSchema,
    routerAddress: addressSchema,
    routerStaticAddress: z.union([addressSchema, z.null()]),
    supportedChains: z.array(nonEmptyStringSchema),
}).strict();
/** One protocol row inside a DeFi summary. */
// `data` stays z.unknown() because each sub-protocol returns its own shape;
// the per-protocol handlers are what validate the inner payload.
export const defiSummaryProtocolSchema = z.object({
    protocol: nonEmptyStringSchema,
    chain: evmChainSlugSchema,
    status: z.enum(['ok', 'error', 'unsupported']),
    data: z.unknown().optional(),
    error: nonEmptyStringSchema.optional(),
}).strict();
/** Response shape for a wallet's DeFi summary. */
export const defiSummaryDataSchema = z.object({
    chain: evmChainSlugSchema,
    address: addressSchema,
    protocols: z.array(defiSummaryProtocolSchema),
    protocolsQueried: z.number().int().nonnegative(),
    timestamp: isoTimestampSchema,
}).strict();
/** Token or spender shape used in approval responses. */
export const approvalPartySchema = z.union([
    addressSchema,
    z.object({
        address: addressSchema,
        name: nonEmptyStringSchema.optional(),
        symbol: nonEmptyStringSchema.optional(),
    }).strict(),
]);
/** One approval entry. */
export const approvalEntrySchema = z.object({
    token: approvalPartySchema,
    spender: approvalPartySchema,
    allowance: nonEmptyStringSchema,
    isUnlimited: z.boolean(),
}).strict();
/** Response shape for wallet approvals. */
export const approvalsDataSchema = z.object({
    chain: evmChainSlugSchema,
    address: addressSchema,
    approvals: z.array(approvalEntrySchema),
}).strict();
/** Response shape for revoke-approval transactions. */
// Branch 1 is the actual handler return shape (unsigned EVM tx). Branch 2 is
// preserved for an alternate protocol-aware shape kept for SDK compatibility.
export const revokeTxDataSchema = z.union([
    z.object({
        to: addressSchema,
        from: addressSchema,
        value: nonEmptyStringSchema,
        data: hexDataSchema,
    }).strict(),
    z.object({
        chain: evmChainSlugSchema,
        token: addressSchema,
        spender: addressSchema,
        tx: z.unknown(),
    }).strict(),
]);
/** Response shape for one Hyperliquid coin's funding data. */
export const hyperliquidFundingRateSchema = z.object({
    coin: nonEmptyStringSchema,
    fundingRate: nonEmptyStringSchema,
    annualizedRate: z.number(),
    openInterest: nonEmptyStringSchema,
    markPrice: nonEmptyStringSchema,
    oraclePrice: nonEmptyStringSchema,
}).strict();
/** Response shape for one dYdX market's funding data. */
export const dydxFundingRateSchema = z.object({
    ticker: nonEmptyStringSchema,
    fundingRate: nonEmptyStringSchema,
    annualizedRate: z.number(),
    price: nonEmptyStringSchema,
    openInterest: nonEmptyStringSchema,
}).strict();
/** Either provider's funding-rate entry. */
export const fundingRateEntrySchema = z.union([
    hyperliquidFundingRateSchema,
    dydxFundingRateSchema,
]);
/** Response shape for the combined funding-rate overview. */
export const fundingRatesDataSchema = z.object({
    hyperliquid: z.array(hyperliquidFundingRateSchema),
    dydx: z.array(dydxFundingRateSchema),
    timestamp: isoTimestampSchema,
}).strict();
/** Response shape for Hyperliquid funding (single coin or full list). */
export const hyperliquidFundingDataSchema = z.union([
    hyperliquidFundingRateSchema,
    z.array(hyperliquidFundingRateSchema),
]);
/** Response shape for dYdX funding (single ticker or full list). */
export const dydxFundingDataSchema = z.union([
    dydxFundingRateSchema,
    z.array(dydxFundingRateSchema),
]);
/** Response shape for a single-provider funding lookup. */
export const singleFundingRateDataSchema = z.union([
    hyperliquidFundingDataSchema,
    dydxFundingDataSchema,
]);
/** Response shape for Avantis vault prices. */
export const avantisVaultPriceDataSchema = z.object({
    chain: evmChainSlugSchema,
    protocol: z.literal('avantis'),
    vault: nonEmptyStringSchema,
    vaultAddress: addressSchema,
    depositPrice: z.object({ USD: z.number() }).strict(),
    redemptionPrice: z.object({ USD: z.number() }).strict(),
    decimals: z.number().int().nonnegative(),
}).strict();
